import { useEffect, useState, useMemo } from "react";
import { API, formatApiError, fmtTime, fmtDate, STATUS_LABELS, STATUS_STYLES } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/components/ui/sonner";
import { BookingDetailsDialog } from "@/pages/CalendarPage";
import { getStaffColor } from "@/utils/staffColors";

function getSourceBadge(source) {
  const s = (source || "").toLowerCase();
  const isSms = s === "public" || s === "online" || s === "sms" || s === "link";

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${
        isSms
          ? "bg-indigo-50 text-indigo-700 border border-indigo-200/60"
          : "bg-slate-100 text-slate-700 border border-slate-200"
      }`}
    >
      {isSms ? "SMS" : "Interne"}
    </span>
  );
}

export default function Appointments() {
  const { tenant } = useAuth();
  const tz = tenant?.timezone || "Europe/Paris";
  const [bookings, setBookings] = useState(null);
  const [status, setStatus] = useState("all");
  const [selectedStaff, setSelectedStaff] = useState("all");
  const [staffOptions, setStaffOptions] = useState([]);
  const [selected, setSelected] = useState(null);

  const load = () => {
    const params = {};
    if (status !== "all") params.status = status;
    API.get("/bookings", { params })
      .then((r) => setBookings(r.data || []))
      .catch((e) => toast.error(formatApiError(e)));
  };

  useEffect(() => {
    load();
  }, [status]);

  // Chargement des employés pour avoir les vrais IDs utilisés dans bookings.employee_id
  useEffect(() => {
    Promise.allSettled([
      API.get("/employees"),
      API.get("/tenant/members")
    ]).then(([empRes, memRes]) => {
      const empData = empRes.status === "fulfilled" ? (empRes.value?.data || []) : [];
      const memData = memRes.status === "fulfilled" ? (memRes.value?.data || []) : [];

      const list = [];
      const seen = new Set();

      // Priorité 1 : les employés (ceux dont l'ID est dans bookings.employee_id)
      empData.forEach((e) => {
        const id = String(e.id);
        const name = e.name || `${e.first_name || ""} ${e.last_name || ""}`.trim() || e.email || "Équipier";
        if (!seen.has(id)) {
          seen.add(id);
          list.push({ id, name, email: e.email, userId: e.user_id });
        }
      });

      // Priorité 2 : les membres d'équipe non présents dans employees
      memData.forEach((m) => {
        const id = String(m.user_id || m.id);
        const name = m.name || m.email || "Membre";
        // Si aucun employé ne correspond à cet email ou nom
        const exists = list.some(item => 
          (m.email && item.email?.toLowerCase() === m.email.toLowerCase()) ||
          (item.name?.toLowerCase() === name.toLowerCase())
        );
        if (!exists && !seen.has(id)) {
          seen.add(id);
          list.push({ id, name, email: m.email, userId: m.user_id });
        }
      });

      setStaffOptions(list);
    });
  }, []);

  useEffect(() => {
    const handler = () => load();
    window.addEventListener("bookings-changed", handler);
    return () => window.removeEventListener("bookings-changed", handler);
  }, []);

  // Filtrage robuste
  const filteredBookings = useMemo(() => {
    if (!bookings) return null;
    if (selectedStaff === "all") return bookings;

    if (selectedStaff === "unassigned") {
      return bookings.filter((b) => !b.employee_id);
    }

    const targetStaff = staffOptions.find(
      (s) => String(s.id) === String(selectedStaff) || String(s.userId) === String(selectedStaff)
    );

    const targetName = (targetStaff?.name || "").trim().toLowerCase();
    const targetEmail = (targetStaff?.email || "").trim().toLowerCase();
    const targetId = String(selectedStaff).toLowerCase();
    const targetUserId = targetStaff?.userId ? String(targetStaff.userId).toLowerCase() : null;

    return bookings.filter((b) => {
      const bEmpId = String(b.employee_id || "").toLowerCase();
      const bEmpName = String(b.employee_name || "").trim().toLowerCase();

      // 1. Correspondance sur l'ID de l'employé
      if (bEmpId && (bEmpId === targetId || (targetUserId && bEmpId === targetUserId))) {
        return true;
      }

      // 2. Correspondance sur le nom si employee_name est renseigné
      if (targetName && bEmpName && (bEmpName.includes(targetName) || targetName.includes(bEmpName))) {
        return true;
      }

      // 3. Correspondance sur l'email
      if (targetEmail && bEmpName && (bEmpName.includes(targetEmail) || targetEmail.includes(bEmpName))) {
        return true;
      }

      return false;
    });
  }, [bookings, selectedStaff, staffOptions]);

  // Fonction utilitaire pour trouver le nom à afficher dans le tableau
  const getEmployeeDisplayName = (b) => {
    if (b.employee_name) return b.employee_name;
    if (b.employee_id) {
      const match = staffOptions.find(
        (s) => String(s.id).toLowerCase() === String(b.employee_id).toLowerCase()
      );
      if (match) return match.name;
    }
    return null;
  };

  return (
    <div className="space-y-4" data-testid="appointments-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Rendez-vous</h1>
          <p className="mt-0.5 text-xs text-slate-500">Historique complet et sources des prises de rendez-vous.</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Menu déroulant de filtre par équipier */}
          <Select value={selectedStaff} onValueChange={setSelectedStaff}>
            <SelectTrigger className="w-48 h-9 text-xs bg-white" data-testid="filter-staff">
              <SelectValue placeholder="Tous les équipiers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">👥 Tous les équipiers</SelectItem>
              {staffOptions.map((s) => (
                <SelectItem key={s.id} value={String(s.id)}>
                  👤 {s.name}
                </SelectItem>
              ))}
              <SelectItem value="unassigned">⚪ Non assigné</SelectItem>
            </SelectContent>
          </Select>

          {/* Filtre par statut */}
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-44 h-9 text-xs bg-white" data-testid="filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(STATUS_LABELS || {}).map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`filter-status-${k}`}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-xs">
        <table className="w-full min-w-[760px] text-sm" data-testid="appointments-table">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/70 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Heure</th>
              <th className="px-4 py-3">Client</th>
              <th className="px-4 py-3">Prestation</th>
              <th className="px-4 py-3">Employé</th>
              <th className="px-4 py-3">Source</th>
              <th className="px-4 py-3">Statut</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {filteredBookings === null ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">Chargement...</td></tr>
            ) : filteredBookings.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400" data-testid="appointments-empty">Aucun rendez-vous.</td></tr>
            ) : (
              filteredBookings.map((b) => {
                const displayName = getEmployeeDisplayName(b);
                const staffColor = getStaffColor(b.employee_id || displayName);

                return (
                  <tr key={b.id} className="cursor-pointer hover:bg-slate-50/80 transition-colors" onClick={() => setSelected(b)} data-testid={`appointment-row-${b.id}`}>
                    <td className="px-4 py-3 capitalize text-slate-600">{fmtDate(b.start_at || b.start_time, tz, { weekday: "short" })}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{fmtTime(b.start_at || b.start_time, tz)}</td>
                    <td className="px-4 py-3">
                      <p className="font-semibold text-slate-900">{b.customer_name || "Client"}</p>
                      <p className="text-xs text-slate-400">{b.customer_phone}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-700 font-medium">{b.service_name || "Prestation"}</td>
                    <td className="px-4 py-3">
                      {displayName ? (
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${staffColor.bg} ${staffColor.border} ${staffColor.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${staffColor.dot}`} />
                          {displayName}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic text-xs">Non assigné</span>
                      )}
                    </td>
                    <td className="px-4 py-3">{getSourceBadge(b.source)}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${(STATUS_STYLES && STATUS_STYLES[b.status]) || "bg-slate-100 text-slate-700"}`}>
                        {(STATUS_LABELS && STATUS_LABELS[b.status]) || b.status}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <BookingDetailsDialog 
        booking={selected} 
        open={Boolean(selected)} 
        onOpenChange={(val) => { if (!val) setSelected(null); }}
        onClose={() => setSelected(null)} 
        onUpdated={() => { setSelected(null); load(); }} 
        onStatusChange={() => { load(); }}
        tz={tz} 
      />
    </div>
  );
}